import '../text-and-lang/as-text.ts';
import '../parameters/as-param-group.ts';
import '../base/buttons/as-button.ts';
import '../overlays/as-hovercard-wrapper.ts';
import '../overlays/as-hovercard.ts';
import { AsFileInput, warnFileSelectedListenerDeprecated, type AsFileHandle, type RemoveFileCallback } from './as-file-input.ts';
import { liveAppData } from '../../store/store.ts';
import styleText from './as-import-group.css?raw';

// Every as-file-input attribute is mirrored from the group host onto the inner
// upload, so the group can be configured declaratively (category, accept,
// trcad-param, configure-on-change, minimal-file-list, ...).
const UPLOAD_PASSTHROUGH_ATTRS: readonly string[] = AsFileInput.observedAttributes;

// as-hovercard-wrapper config attributes. When a child <as-hovercard> is
// adopted into one of the group's wrappers, these are mirrored from the card
// onto its wrapper, so consumers tune placement etc. on the card they author
// (the wrappers themselves live inside the group's generated markup).
const HOVER_WRAPPER_CONFIG_ATTRS: readonly string[] = [
  'placement', 'offset', 'delay', 'hide-delay', 'show-on-hover', 'toggle-on-click', 'keep-open',
];

/**
 * <as-import-group>
 *
 * Drop-in import card: renders an <as-param-group> in light DOM (so it
 * participates directly in the parent layout, and inner events bubble up
 * through the host unchanged) containing:
 *
 *   - subheading        "Import {subject}" (default: "Import Workpiece")
 *   - hint text         "Recommended format: .STL" (<as-text>)
 *   - <as-file-input>   the drop zone / file picker, in a hovercard wrapper
 *   - <as-button>       "Try sample data", in a hovercard wrapper
 *
 * Attributes
 *   id                      Optional. The inner file input gets `<id>-file-input`
 *                           and the sample button `<id>-sample-button`. When
 *                           absent, an id is derived and reflected back onto the
 *                           host: the trcad-param value if set; otherwise the id
 *                           of the parent element (or the closest <as-step-panel>
 *                           with an id) plus "-import-group", numbered by
 *                           position when that container holds several import
 *                           groups (e.g. "step-1-import-group-2").
 *   subject-default-text    Subject word in the heading (default: "Workpiece")
 *   subject-dict-key        Localization key for the subject word
 *   subheading-default-text Heading template; "{0}" / "{}" is replaced by the
 *                           subject (default: "Import {0}")
 *   subheading-dict-key     Localization key for the heading template — its
 *                           value should contain "{0}" for the subject, which
 *                           is passed via subheading-vars
 *   hint-default-text       Hint line (default: "Recommended format: .STL")
 *   hint-dict-key           Localization key for the hint line
 *   no-sample-button        Boolean. Hides the "Try sample data" button row
 *
 * All <as-file-input> attributes (category, accept, multiple, trcad-param,
 * configure-on-change, no-accepts-label, minimal-file-list, ...) are passed
 * through to the inner upload, so the group is a drop-in replacement for a
 * bare <as-file-input>. In the GrabCAD env (liveAppData.appEnv === 'grabcad')
 * the "Accepts:" hint is hidden automatically — imports go through GrabCAD's
 * own open/convert dialog there.
 *
 * Hovercards
 *   The drop zone and the sample button are each wrapped in an
 *   <as-hovercard-wrapper>. Author an <as-hovercard> as a light-DOM child of
 *   the group and it is adopted into the matching wrapper, selected by its
 *   `for` attribute: "file-input" or "sample-button". Cards without `for` fill
 *   in order — the first goes to the file input, the next to the sample
 *   button. Wrapper config attributes (placement, offset, delay, hide-delay,
 *   show-on-hover, toggle-on-click, keep-open) set on the card are forwarded
 *   to its wrapper.
 *
 * Events (all bubble + composed, dispatched by the inner components through
 * this light-DOM host)
 *   as-button-click         From the sample button (buttonName
 *                           "try-sample-data", id `<id>-sample-button`)
 *   as-files-selected / as-files-change / as-file-removed / change / ...
 *                           Re-broadcast naturally from the inner
 *                           <as-file-input> — see its docs (as-file-selected is
 *                           a deprecated alias of as-files-change).
 *                           For removal there is also a callback form:
 *                           group.onRemoveFile((removedFile, fileList) => ...)
 *
 * Element access (for configuring the parts directly)
 *   group.fileInput               the inner <as-file-input>
 *   group.files                   convenience: the inner file input's current
 *                                 selection (AsFileHandle[], empty when none)
 *   group.sampleButton            the inner "Try sample data" <as-button>
 *   group.paramGroup              the wrapping <as-param-group>
 *   group.fileHovercardWrapper    the wrapper around the file input
 *   group.sampleHovercardWrapper  the wrapper around the sample button
 */
export class AsImportGroup extends HTMLElement {
  #built = false;
  #groupEl: HTMLElement | null = null;
  #hintEl: HTMLElement | null = null;
  #fileInputEl: AsFileInput | null = null;
  #sampleBtnEl: HTMLElement | null = null;
  #fileHoverEl: HTMLElement | null = null;
  #sampleHoverEl: HTMLElement | null = null;
  #removeEnvEffect: (() => void) | null = null;
  // Last id this component derived and reflected itself; while the attribute
  // still equals it, the id stays auto-managed (re-derived when trcad-param or
  // DOM position changes). An author writing any other id takes ownership.
  #autoId: string | null = null;
  #syncingId = false;
  #fallbackId: string | null = null;
  #hovercardObserver: MutationObserver | null = null;
  // Held until _build creates the inner file input, then forwarded to it.
  #removeFileCb: RemoveFileCallback | RemoveFileCallback[] | null = null;

  private static _styleInjected = false;
  private static _autoIdSeq = 0;

  static readonly observedAttributes = [
    'id',
    'subheading-dict-key', 'subheading-default-text',
    'subject-dict-key', 'subject-default-text',
    'hint-dict-key', 'hint-default-text',
    ...UPLOAD_PASSTHROUGH_ATTRS,
  ];

  override addEventListener(type: string, listener: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions): void {
    if (type === 'as-file-selected') warnFileSelectedListenerDeprecated(this.tagName.toLowerCase());
    super.addEventListener(type, listener, options);
  }

  get fileInput(): AsFileInput | null { return this.#fileInputEl; }
  /** @deprecated Use `fileInput` instead. */
  get fileUpload(): AsFileInput | null {
    console.warn('as-import-group: the `fileUpload` getter is deprecated, use `fileInput` instead');
    return this.#fileInputEl;
  }
  /** The inner file input's current selection (empty when none). */
  get files(): readonly AsFileHandle[] { return this.#fileInputEl?.files ?? []; }

  /** Register remove-file callback(s) on the inner file input (see
   *  AsFileInput.onRemoveFile / the as-file-removed event). Accepts a single
   *  callback or an array; pass null to unregister. Safe to call before the
   *  group is connected; the callbacks are forwarded once built. */
  onRemoveFile(cb: RemoveFileCallback | RemoveFileCallback[] | null): void {
    this.#removeFileCb = cb;
    this.#fileInputEl?.onRemoveFile(cb);
  }
  get sampleButton(): HTMLElement | null { return this.#sampleBtnEl; }
  get paramGroup(): HTMLElement | null { return this.#groupEl; }
  get fileHovercardWrapper(): HTMLElement | null { return this.#fileHoverEl; }
  get sampleHovercardWrapper(): HTMLElement | null { return this.#sampleHoverEl; }

  connectedCallback(): void {
    if (!AsImportGroup._styleInjected) {
      const style = document.createElement('style');
      style.textContent = styleText;
      style.setAttribute('data-component', 'as-import-group');
      document.head.appendChild(style);
      AsImportGroup._styleInjected = true;
    }
    if (!this.#built) this._build();
    this._syncSubheading();
    this._syncHint();
    this._syncId();
    this._adoptHovercards();
    // Hovercard children may be parsed (or appended) after this connect ran;
    // adopt them into their wrapper whenever they show up.
    this.#hovercardObserver ??= new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof HTMLElement && node.tagName.toLowerCase() === 'as-hovercard') {
            this._adoptHovercard(node);
          }
        }
      }
    });
    this.#hovercardObserver.observe(this, { childList: true });
    // env can be set late (GrabCAD org detection after userData), so re-sync
    // the accepts hint when it arrives.
    this.#removeEnvEffect = liveAppData.addSideEffect('appEnv', () => this._syncUploadAttr('no-accepts-label'));
    this._syncUploadAttr('no-accepts-label');
  }

  disconnectedCallback(): void {
    this.#removeEnvEffect?.();
    this.#removeEnvEffect = null;
    this.#hovercardObserver?.disconnect();
  }

  attributeChangedCallback(name: string, _old: string | null, _new: string | null): void {
    if (!this.#built) return;
    if (name === 'id') {
      this._syncId();
    } else if (UPLOAD_PASSTHROUGH_ATTRS.includes(name)) {
      this._syncUploadAttr(name);
      if (name === 'trcad-param') this._syncId();
    } else if (name.startsWith('hint-')) {
      this._syncHint();
    } else {
      this._syncSubheading();
    }
  }

  private _build(): void {
    this.#built = true;
    const tpl = document.createElement('template');
    tpl.innerHTML = /*html*/`
      <as-param-group>
        <as-text class="import-hint"></as-text>
        <as-hovercard-wrapper class="import-file-hover" show-on-hover placement="right">
          <as-file-input></as-file-input>
        </as-hovercard-wrapper>
        <div class="import-sample-row">
          <as-hovercard-wrapper class="import-sample-hover" show-on-hover placement="top">
            <as-button
              name="try-sample-data"
              default-text="Try sample data"
              no-default-icon>
            </as-button>
          </as-hovercard-wrapper>
        </div>
      </as-param-group>
    `;
    const frag = tpl.content;
    this.#groupEl       = frag.querySelector('as-param-group') as HTMLElement;
    this.#hintEl        = frag.querySelector('.import-hint') as HTMLElement;
    this.#fileInputEl      = frag.querySelector('as-file-input') as AsFileInput;
    this.#sampleBtnEl   = frag.querySelector('as-button') as HTMLElement;
    this.#fileHoverEl   = frag.querySelector('.import-file-hover') as HTMLElement;
    this.#sampleHoverEl = frag.querySelector('.import-sample-hover') as HTMLElement;

    for (const attr of UPLOAD_PASSTHROUGH_ATTRS) this._syncUploadAttr(attr);
    if (this.#removeFileCb) this.#fileInputEl?.onRemoveFile(this.#removeFileCb);

    this.appendChild(frag);
  }

  /** Move author-provided <as-hovercard> children into their target wrapper. */
  private _adoptHovercards(): void {
    for (const child of Array.from(this.children)) {
      if (child instanceof HTMLElement && child.tagName.toLowerCase() === 'as-hovercard') {
        this._adoptHovercard(child);
      }
    }
  }

  private _adoptHovercard(card: HTMLElement): void {
    const target = card.getAttribute('for');
    let wrapper: HTMLElement | null;
    if (target === 'sample-button' || target === 'try-sample-data' || (target && target === this.#sampleBtnEl?.id)) {
      wrapper = this.#sampleHoverEl;
    } else if (target) {
      wrapper = this.#fileHoverEl;
    } else {
      // No explicit target: fill in order — file input first, sample button
      // next (the common authoring shape is one card for each).
      wrapper = this.#fileHoverEl?.querySelector('as-hovercard') ? this.#sampleHoverEl : this.#fileHoverEl;
    }
    if (!wrapper || card.parentElement === wrapper) return;
    for (const attr of HOVER_WRAPPER_CONFIG_ATTRS) {
      const value = card.getAttribute(attr);
      if (value !== null) wrapper.setAttribute(attr, value);
    }
    wrapper.appendChild(card);
  }

  /**
   * Resolve the group's effective id and stamp `<id>-file-input` /
   * `<id>-sample-button` on the inner elements. A derived id is reflected onto
   * the host attribute so it is visible in the DOM and stable for consumers.
   */
  private _syncId(): void {
    if (!this.#built || this.#syncingId) return;
    const explicit = this.getAttribute('id');
    let id = explicit;
    if (!id || id === this.#autoId) {
      id = this._deriveId();
      this.#autoId = id;
      if (explicit !== id) {
        this.#syncingId = true;
        try { this.setAttribute('id', id); } finally { this.#syncingId = false; }
      }
    } else {
      this.#autoId = null;
    }
    if (this.#fileInputEl) this.#fileInputEl.id = `${id}-file-input`;
    if (this.#sampleBtnEl) this.#sampleBtnEl.id = `${id}-sample-button`;
  }

  private _deriveId(): string {
    const param = this.getAttribute('trcad-param');
    if (param) return param;

    const parent = this.parentElement;
    const stepPanel = this.closest('as-step-panel');
    const container = parent?.id ? parent : (stepPanel?.id ? stepPanel : null);
    if (container) {
      const groups = Array.from(container.querySelectorAll('as-import-group'));
      const index = groups.indexOf(this);
      const suffix = groups.length > 1 ? `-${index + 1}` : '';
      return `${container.id}-import-group${suffix}`;
    }

    this.#fallbackId ??= `import-group-${++AsImportGroup._autoIdSeq}`;
    return this.#fallbackId;
  }

  private _syncUploadAttr(name: string): void {
    if (!this.#fileInputEl) return;
    let value = this.getAttribute(name);
    // In the GrabCAD env imports go through GrabCAD's own open/convert dialog,
    // so the browser-style "Accepts:" hint is noise — hide it there even
    // without the host attribute.
    if (name === 'no-accepts-label' && value === null && liveAppData.appEnv === 'grabcad') value = '';
    if (value === null) this.#fileInputEl.removeAttribute(name);
    else this.#fileInputEl.setAttribute(name, value);
  }

  private _syncSubheading(): void {
    if (!this.#groupEl) return;
    const template = this.getAttribute('subheading-default-text') ?? 'Import {0}';
    const subjectDefault = this.getAttribute('subject-default-text') ?? 'Workpiece';
    this.#groupEl.setAttribute('subheading-default-text', template.replace(/\{0\}|\{\}/g, subjectDefault));

    const dictKey = this.getAttribute('subheading-dict-key');
    if (dictKey) {
      const subjectDictKey = this.getAttribute('subject-dict-key');
      const subjectVar = subjectDictKey
        ? { dictKey: subjectDictKey, defaultText: subjectDefault }
        : subjectDefault;
      this.#groupEl.setAttribute('subheading-vars', JSON.stringify([subjectVar]));
      this.#groupEl.setAttribute('subheading-dict-key', dictKey);
    } else {
      this.#groupEl.removeAttribute('subheading-dict-key');
      this.#groupEl.removeAttribute('subheading-vars');
    }
  }

  private _syncHint(): void {
    if (!this.#hintEl) return;
    this.#hintEl.textContent = this.getAttribute('hint-default-text') ?? 'Recommended format: .STL';
    const dictKey = this.getAttribute('hint-dict-key');
    if (dictKey) this.#hintEl.setAttribute('dict-key', dictKey);
    else this.#hintEl.removeAttribute('dict-key');
  }
}

if (!customElements.get('as-import-group')) {
  customElements.define('as-import-group', AsImportGroup);
} else {
  console.warn('as-import-group already defined');
}
